import { useMemo } from 'react'
import { Autocomplete } from '~/@/components/inputs/selection/autocomplete'
import { BreadcrumbObjectType } from '~/common/generated/apollo-operations'
import { DossIcon } from '~/components/DossIcon'
import { WorkspaceIcon } from '~/components/navigation/WorkspaceIcon'
import { AutocompleteTable } from '~/components/shared/Table/CustomFilterPanelRow'

export type TableAutocompleteProps = {
  tables: AutocompleteTable[]
  onChange?: (table: AutocompleteTable | null) => void
  selectedTable?: AutocompleteTable | null
  disabled?: boolean
  className?: string
}

/**
 * Dropdown component for selecting a table
 */
export function TableAutocomplete({
  tables,
  selectedTable,
  onChange,
  disabled,
  className,
}: TableAutocompleteProps) {
  const adornment = selectedTable ? (
    <WorkspaceIcon
      icon={selectedTable.icon ? <DossIcon name={selectedTable.icon.name} size={12} /> : undefined}
      size={12}
      workspaceType={BreadcrumbObjectType.TABLE}
    />
  ) : (
    <WorkspaceIcon size={12} workspaceType={BreadcrumbObjectType.PROJECT} />
  )

  const getTableLabel = (table: AutocompleteTable) => {
    return table.parentTable ? `${table.parentTable.name} / ${table.name}` : table.name
  }
  const getTableStartIcon = (table: AutocompleteTable) => {
    return (
      <WorkspaceIcon
        icon={table.icon ? <DossIcon name={table.icon.name} size={12} /> : undefined}
        size={12}
        workspaceType={BreadcrumbObjectType.TABLE}
      />
    )
  }

  const tableOptions = useMemo(
    () =>
      tables.map((table) => {
        const label = getTableLabel(table)
        const startIcon = getTableStartIcon(table)
        return {
          value: table.id,
          label,
          startIcon,
        }
      }),
    [tables],
  )

  return (
    <div className="relative flex flex-col gap-0">
      <div className="flex flex-col gap-2 text-gray-9">
        <Autocomplete
          className={className}
          disabled={disabled}
          ignoreDropdownStyles
          onChange={(newValue) => {
            onChange?.(tables.find((table) => table.id === newValue?.value) || null)
          }}
          options={tableOptions}
          startAdornment={adornment}
          value={
            selectedTable
              ? {
                  value: selectedTable.id,
                  label: getTableLabel(selectedTable),
                  startIcon: getTableStartIcon(selectedTable),
                }
              : undefined
          }
        />
      </div>
    </div>
  )
}
